import { create } from 'zustand';
import type { GameVariant, TimeControlPreset, ChallengeColor, GameMode, LinkChallengeInfo, LobbyCounts, MatchLegs, MatchType, OpeningMode } from '@draughts/shared';
import { clearStoredChallengeLinkId } from '../utils/challenge-link-storage';
import { log } from '../utils/logger';

export interface OnlineUser {
  userId: string;
  username: string;
  avatar?: string | null;
  /** `null` per ospiti e bot: non hanno punteggio, e la riga non ne mostra. */
  rating: number | null;
  /**
   * `busy` = ha chiesto di non essere disturbato: niente sfide dirette e
   * niente inviti di rivincita fuori partita. Lo deriva il server
   * (`statoPubblico`), non si scrive mai a mano.
   */
  status: 'available' | 'playing' | 'away' | 'busy';
  /**
   * Ospite — e i bot ci stanno dentro, perché sul filo un bot È un ospite.
   * Opzionale solo per i client su un bundle precedente; il fallback è il
   * prefisso `Guest_`.
   */
  isGuest?: boolean;
  /**
   * Con chi sta giocando, quando sta giocando.
   *
   * Lo compone il server — la lista dei collegati non conosce le stanze — e
   * arriva per due strade: la risposta a `online:list`, che lo ricava sempre,
   * e l'annuncio `online:update`, che lo porta quando chi annuncia ce l'ha in
   * mano. Senza, il badge dice «In partita» e basta: mai una bugia, solo meno
   * informazione, e al giro successivo della lista il nome compare.
   */
  opponent?: string;
  /**
   * Per quale variante+cadenza è stato letto `rating` (`italian:blitz`).
   *
   * Serve a distinguere una riga arrivata dalla lista — dove il punteggio è
   * quello del bucket chiesto — da una arrivata dal broadcast `online:update`,
   * che è uno per tutti e non può sapere quale punteggio guarda ciascun
   * destinatario. Quella riga arriva senza timbro e non mostra numero finché
   * non la si rilegge: meglio nessun numero che il numero di un'altra variante.
   */
  ratingBucket?: string;
  /**
   * Su cosa sta giocando: variante e cadenza di quella partita.
   *
   * Arriva per UNA strada sola — la risposta a `online:list`, l'unica che abbia
   * le stanze sotto mano — e non dall'annuncio `online:update`, che si scrive a
   * mano in diciassette punti. Quindi una riga passata a `playing` per annuncio
   * ce l'ha assente, e la riga della coppia dice «Gioca» senza pastiglia finché
   * il giro successivo della lista non gliela dà: mai una bugia, solo meno
   * informazione. Stessa scelta di `opponent` qui sopra, e per lo stesso motivo.
   */
  game?: { variant: GameVariant; timeControl: TimeControlPreset | 'none' };
}

/**
 * An incoming rematch invite from a player still on their game-over screen,
 * delivered to a player who has already left the game. Surfaced globally
 * (like a direct challenge) via GlobalIncomingRematchBanner.
 */
export interface RematchInvite {
  roomId: string;
  fromUserId?: string;
  fromUsername: string;
  /** `null` per ospiti e bot: nessun punteggio da mostrare. */
  fromRating: number | null;
  variant: GameVariant;
  timeControl: TimeControlPreset | 'none';
  expiresAt: number;
}

export interface Challenge {
  id: string;
  fromUserId: string;
  fromUsername: string;
  /** `null` per ospiti e bot: nessun punteggio da mostrare. */
  fromRating: number | null;
  toUserId: string;
  toUsername: string;
  /**
   * Il punteggio di CHI RICEVE, nella variante e nella cadenza della sfida.
   *
   * Viaggiava gia' sul filo (il server manda l'oggetto sfida intero) ma qui non
   * era dichiarato, quindi la finestra «in attesa di risposta» mostrava il nome
   * dell'avversario e basta. `null` per un ospite o un bot, e per una partita
   * senza orologio: non esiste una colonna di rating per la cadenza ∞.
   */
  toRating: number | null;
  variant: GameVariant;
  timeControl: TimeControlPreset | 'none';
  color: ChallengeColor;
  /** Who may accept, and whether it counts. Absent on a challenge from an old server. */
  matchType?: MatchType;
  rated: boolean;
  gameMode: GameMode;
  opening: OpeningMode;
  /** 2 = andata e ritorno (drawn openings only), 1 = solo andata. */
  legs: MatchLegs;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled' | 'expired';
  createdAt: number;
  expiresAt: number;
}

/** Why a challenge link stopped being playable, for the screen still showing it. */
export type LinkChallengeNotice = 'cancelled' | 'expired' | null;

interface OnlineStore {
  // State
  onlineUsers: OnlineUser[];
  /**
   * Chi è collegato, spaccato come lo mostrano hero e lobby. I bot stanno fra
   * gli ospiti (lato server, `getCounts`). Vive QUI e non in lobby-store perché
   * ormai lo legge anche la hero, che con la lobby non c'entra.
   */
  counts: LobbyCounts;
  isLoadingUsers: boolean;

  // Pending challenge states
  outgoingChallenge: Challenge | null;
  incomingChallenge: Challenge | null;

  /** Shareable link I created and am waiting on (challenger side). */
  linkChallenge: LinkChallengeInfo | null;
  /** Link I accepted from a friend and am waiting on (invited side). */
  pendingLinkChallenge: LinkChallengeInfo | null;
  /** Set when either of the two above died while its screen was open. */
  linkChallengeNotice: LinkChallengeNotice;

  // Incoming rematch invite (from a player still on their game-over screen,
  // shown globally to a player who already left the game)
  incomingRematch: RematchInvite | null;

  // Actions
  setOnlineUsers: (users: OnlineUser[]) => void;
  setCounts: (counts: LobbyCounts) => void;
  addOnlineUser: (user: OnlineUser) => void;
  removeOnlineUser: (userId: string) => void;
  updateUserStatus: (userId: string, status: OnlineUser['status'], opponent?: string) => void;
  /**
   * Il punteggio di questi utenti e' appena cambiato: le loro righe perdono il
   * timbro del bucket, e chi le mostra (lista dei collegati, avversario scelto
   * nella Sfida diretta) le rilegge da solo, come fa con chi arriva adesso.
   */
  punteggiCambiati: (userIds: string[]) => void;
  setIsLoadingUsers: (isLoading: boolean) => void;
  /**
   * La presenza e' scaduta: nessuno di questi e' piu' «online» per quanto ne
   * sappiamo. Non e' `reset()` — vedi l'attuazione.
   */
  invalida: () => void;

  // Challenge actions
  setOutgoingChallenge: (challenge: Challenge | null) => void;
  setIncomingChallenge: (challenge: Challenge | null) => void;
  clearChallenges: () => void;

  // Challenge-link actions
  setLinkChallenge: (link: LinkChallengeInfo | null) => void;
  setPendingLinkChallenge: (link: LinkChallengeInfo | null) => void;
  setLinkChallengeNotice: (notice: LinkChallengeNotice) => void;
  /**
   * Il flusso link è concluso su questo device: stato e id locale vanno
   * insieme. Chiamata all'inizio partita — l'overlay del link è globale (si
   * disegna anche sopra la gamescreen), quindi nient'altro lo smonta.
   */
  clearLinkChallenges: () => void;

  // Rematch invite actions
  setIncomingRematch: (invite: RematchInvite | null) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  onlineUsers: [] as OnlineUser[],
  counts: { registered: 0, guests: 0 } as LobbyCounts,
  isLoadingUsers: false,
  outgoingChallenge: null as Challenge | null,
  incomingChallenge: null as Challenge | null,
  linkChallenge: null as LinkChallengeInfo | null,
  pendingLinkChallenge: null as LinkChallengeInfo | null,
  linkChallengeNotice: null as LinkChallengeNotice,
  incomingRematch: null as RematchInvite | null,
};

export const useOnlineStore = create<OnlineStore>()((set) => ({
  // Initial state
  ...initialState,

  setOnlineUsers: (users) => {  set({ onlineUsers: users, isLoadingUsers: false }); },

  /*
   * Il filtro PER VALORE, e non e' un'ottimizzazione gratuita.
   *
   * `online:counts` e' un `io.emit` (server/src/lobby/lobby-broadcast.ts): parte
   * a ogni collegamento e a ogni scollegamento, e la flotta dei bot di presenza
   * si rinnova da sola — PRESENCE_TOPUP_INTERVAL_MS 30s e
   * PRESENCE_TABLE_INTERVAL_MS 45s in server/src/bots/bot.config.ts — quindi un
   * client FERMO in home ne riceve uno ogni quindici o trenta secondi. Il carico
   * utile e' sempre un oggetto NUOVO appena uscito dal filo, anche quando i due
   * numeri dentro sono identici, e `useConnectedCount` si abbonava a
   * quell'oggetto: un numero che non era cambiato faceva riconciliare l'intera
   * dashboard, dieci card fra cui una mappa di 91 archi SVG.
   *
   * Tornare lo STESSO oggetto di stato e' il modo che zustand ha di dire «non e'
   * successo niente»: `setState` confronta con `Object.is` e non avvisa nessun
   * ascoltatore. E' la stessa manovra che `addOnlineUser` qui sotto usa per il
   * duplicato.
   */
  setCounts: (counts) => set((state) => (
    state.counts.registered === counts.registered && state.counts.guests === counts.guests
      ? state
      : { counts }
  )),

  addOnlineUser: (user) => {  set((state) => {
    // Don't add duplicates
    if (state.onlineUsers.some(u => u.userId === user.userId)) {
      log.info('OnlineStore', 'addOnlineUser skipped (duplicate)', { userId: user.userId });
      return state;
    }
    return { onlineUsers: [...state.onlineUsers, user] };
  }); },

  removeOnlineUser: (userId) => {  set((state) => ({
    onlineUsers: state.onlineUsers.filter(u => u.userId !== userId),
  })); },

  updateUserStatus: (userId, status, opponent) => {  set((state) => ({
    onlineUsers: state.onlineUsers.map(u =>
      /*
       * Uscire da `playing` CANCELLA l'avversario, sempre: altrimenti la riga
       * continuerebbe a nominare una partita finita. Restarci lo conserva
       * quando l'annuncio non lo porta — l'avversario non puo' cambiare senza
       * passare da `available`, che la fine partita annuncia sempre.
       *
       * E la partita (variante e cadenza) segue la STESSA regola, scritta
       * esplicitamente: qui la riga si ricostruisce con `{...u}`, quindi un
       * campo non nominato sopravvivrebbe per omissione — e una riga tornata
       * `available` si porterebbe dietro la pastiglia di una partita finita.
       * Lasciarla su `playing` serve al caso vero: A finisce con C e
       * ricomincia subito con B su un'altra variante, e in mezzo passa sempre
       * da `available`, che e' dove la pastiglia vecchia muore.
       */
      u.userId === userId
        ? {
          ...u,
          status,
          opponent: status === 'playing' ? (opponent ?? u.opponent) : undefined,
          game: status === 'playing' ? u.game : undefined,
        }
        : u
    ),
  })); },

  punteggiCambiati: (userIds) => {
    const cambiati = new Set(userIds);
    set((state) => ({
      onlineUsers: state.onlineUsers.map((u) =>
        cambiati.has(u.userId) ? { ...u, ratingBucket: undefined } : u
      ),
    }));
  },

  setIsLoadingUsers: (isLoading) => set({ isLoadingUsers: isLoading }),

  /**
   * Svuotamento CHIRURGICO: solo quello che il server ci aveva prestato.
   *
   * Lo chiama `invalidaPresenza` nel multiplayer-store. `reset()` non va bene
   * qui perche' porterebbe via anche le sfide (`outgoing`/`incoming`), le sfide
   * a link e la rivincita in arrivo: quelle hanno un ciclo di vita proprio, e
   * una caduta di rete non e' un rifiuto.
   *
   * I `counts` azzerati sono cio' che smette di congelare la pillola «N Online»
   * della hero, che prima restava all'ultimo numero visto per sempre.
   * `useConnectedCount` sottrae uno (il server conta anche chi legge) ma ha gia'
   * il suo `Math.max(0, ...)`, quindi lo zero non diventa un meno uno.
   */
  invalida: () => set({
    onlineUsers: [],
    counts: { registered: 0, guests: 0 },
    isLoadingUsers: false,
  }),

  setOutgoingChallenge: (challenge) => { log.info('OnlineStore', 'Outgoing challenge set', { toUsername: challenge?.toUsername }); set({ outgoingChallenge: challenge }); },

  setIncomingChallenge: (challenge) => { log.info('OnlineStore', 'Incoming challenge received', { fromUsername: challenge?.fromUsername }); set({ incomingChallenge: challenge }); },

  clearChallenges: () => { log.info('OnlineStore', 'Challenges cleared'); set({ outgoingChallenge: null, incomingChallenge: null }); },

  setLinkChallenge: (link) => { log.info('OnlineStore', 'Challenge link set', { linkId: link?.id, status: link?.status }); set({ linkChallenge: link }); },

  setPendingLinkChallenge: (link) => { log.info('OnlineStore', 'Pending challenge link set', { linkId: link?.id, status: link?.status }); set({ pendingLinkChallenge: link }); },

  setLinkChallengeNotice: (notice) => set({ linkChallengeNotice: notice }),

  clearLinkChallenges: () => {
    log.info('OnlineStore', 'Challenge links cleared');
    // L'id salvato va con lo stato: lasciarlo permetterebbe all'effetto di
    // restore in App.tsx di rimettere l'overlay sopra una scacchiera viva.
    clearStoredChallengeLinkId();
    set({ linkChallenge: null, pendingLinkChallenge: null, linkChallengeNotice: null });
  },

  setIncomingRematch: (invite) => { log.info('OnlineStore', 'Incoming rematch invite', { fromUsername: invite?.fromUsername, roomId: invite?.roomId }); set({ incomingRematch: invite }); },

  reset: () => {  set(initialState); },
}));
